import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark, faSpinner, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/shared/PhoneInput';
import { useDebounce } from '@/hooks/use-debounce';
import { customerService, type FieldValidation } from '@/services/customer.service';
import { isValidEmail, isValidUsername } from '@/lib/identity';

type ValidateField = 'customer-code' | 'email' | 'phone' | 'username' | 'gst';

/**
 * Text input with live (500ms-debounced) server-side duplicate validation.
 *
 * Extra client-side rules applied before the API call:
 *  - email    → format validated; "Invalid email format" shown, no API call until valid.
 *  - username → auto-lowercased on input; pattern [a-z0-9_] min 3 chars enforced.
 *  - phone    → rendered as PhoneInput (digits-only with country-code selector).
 */
export function ValidatedField({
  label,
  id,
  field,
  value,
  onChange,
  excludeId,
  required,
  disabled,
  placeholder,
  type = 'text',
}: {
  label: string;
  id: string;
  field: ValidateField;
  value: string;
  onChange: (v: string) => void;
  excludeId?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  type?: string;
}) {
  const debounced = useDebounce(value.trim(), 500);

  // Client-side format check — gates the API call
  const formatError: string | null = (() => {
    if (!debounced) return null;
    if (field === 'email' && !isValidEmail(debounced)) return 'Invalid email format';
    if (field === 'username' && !isValidUsername(debounced))
      return 'Only lowercase letters, numbers, underscore — min 3 chars';
    return null;
  })();

  const enabled = debounced.length >= 1 && !disabled && formatError === null;

  const { data, isFetching } = useQuery<FieldValidation>({
    queryKey: ['customer-validate', field, debounced, excludeId ?? null],
    queryFn: () => customerService.validate(field, debounced, excludeId),
    enabled,
    staleTime: 10_000,
  });

  let status: React.ReactNode = null;
  if (formatError) {
    status = (
      <span className="flex items-center gap-1 text-xs text-destructive">
        <FontAwesomeIcon icon={faXmark} className="h-3 w-3" /> {formatError}
      </span>
    );
  } else if (enabled && isFetching) {
    status = (
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" /> Checking…
      </span>
    );
  } else if (enabled && data) {
    if (!data.available) {
      let conflictMsg: string;
      if (data.source === 'USER') {
        conflictMsg = data.existingName
          ? `Already in use by staff account — ${data.existingName}`
          : 'Already in use by a staff account';
      } else {
        conflictMsg = data.existingName
          ? `Already exists — ${data.existingName}`
          : 'Already exists';
      }
      status = (
        <span className="flex items-center gap-1 text-xs text-destructive">
          <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
          {conflictMsg}
        </span>
      );
    } else if (data.warning && data.duplicate) {
      status = (
        <span className="flex items-center gap-1 text-xs text-amber-600">
          <FontAwesomeIcon icon={faTriangleExclamation} className="h-3 w-3" />
          Warning: also used by {data.existingName ?? 'another record'}
        </span>
      );
    } else {
      status = (
        <span className="flex items-center gap-1 text-xs text-emerald-600">
          <FontAwesomeIcon icon={faCheck} className="h-3 w-3" /> Available
        </span>
      );
    }
  }

  const handleChange = (raw: string) => {
    onChange(field === 'username' ? raw.toLowerCase() : raw);
  };

  return (
    <FormField label={label} htmlFor={id} required={required}>
      {field === 'phone' ? (
        <PhoneInput id={id} value={value} onChange={onChange} placeholder={placeholder} disabled={disabled} />
      ) : (
        <Input
          id={id}
          type={type}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
        />
      )}
      <div className="min-h-[1rem]">{status}</div>
    </FormField>
  );
}

/** Non-blocking helper for the form: returns true if a field is a hard conflict. */
export function isBlockingConflict(v: FieldValidation | undefined): boolean {
  return !!v && !v.available && !v.warning;
}
